import { PersonaFieldTypes } from '@personas/enums/persona-fields-types.enum'
import { FormFieldLibraryItem, FormFieldTypeId } from './form-field-library.types'

/** Default Frequently used (business-card) field types — Figma empty/default order. */
export const DEFAULT_FREQUENTLY_USED_TYPE_IDS: FormFieldTypeId[] = [
  PersonaFieldTypes.FULLNAME,
  PersonaFieldTypes.PHONE,
  PersonaFieldTypes.EMAIL,
  PersonaFieldTypes.JOB_TITLE,
  PersonaFieldTypes.ORGANIZATION
]

/** Fixed core fields always kept in Frequently used (do not get replaced by aggregation). */
export const FIXED_FREQUENTLY_USED_TYPE_IDS: FormFieldTypeId[] = [...DEFAULT_FREQUENTLY_USED_TYPE_IDS]

export const MAX_FREQUENTLY_USED = 8

/**
 * Coming-soon `form_element_type_id` values — start after 44.
 * Note: PersonaFieldTypes.AI_FACIAL_RECOGNITION already uses 45, so reserved ids begin at 46.
 * Keys use snake_case to match library data naming.
 */
export const ComingSoonFieldTypes = {
  address: 46,
  category: 47,
  chest_circumference: 48,
  current_age: 49,
  data_consent: 50,
  date_of_birth: 51,
  document_number: 52,
  education_level: 53,
  error_message: 54,
  eye_colour: 55,
  flag: 56,
  hair_colour: 57,
  height: 58,
  money: 59,
  nationality: 60,
  number: 61,
  shoe_size: 62,
  skin_tone: 63,
  t_shirt_size: 64,
  upload_document: 65,
  upload_photo: 66,
  url: 67,
  waist_circumference: 68,
  weight: 69
} as const

/**
 * Hard-coded MICEtribe field catalogue (icons, names, placeholders, help, coming soon).
 * Available types use PersonaFieldTypes; coming-soon use ComingSoonFieldTypes (≥ 45).
 */
export const FORM_FIELD_LIBRARY: FormFieldLibraryItem[] = [
  {
    form_element_type_id: ComingSoonFieldTypes.address,
    name: 'Address',
    name_ar: 'العنوان',
    placeholder: 'e.g. 123 Main St',
    help_text: 'Collect a postal address.',
    coming_soon: true,
    icon: 'home',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.AGE_GROUP,
    name: 'Age group',
    name_ar: 'الفئة العمرية',
    placeholder: 'Select age group',
    help_text: 'Collect age group selection.',
    coming_soon: false,
    icon: 'users',
    estimated_filling_time_seconds: 10
  },
  {
    form_element_type_id: ComingSoonFieldTypes.category,
    name: 'Category',
    name_ar: 'الفئة',
    placeholder: 'Select category',
    help_text: 'Collect a category.',
    coming_soon: true,
    icon: 'category',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.chest_circumference,
    name: 'Chest circumference',
    name_ar: 'محيط الصدر',
    placeholder: 'e.g. 90 cm',
    help_text: 'Collect chest circumference.',
    coming_soon: true,
    icon: 'chest-circumference',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.CITY,
    name: 'City of residence',
    name_ar: 'مدينة الإقامة',
    placeholder: 'Select city',
    help_text: 'Collect city of residence.',
    coming_soon: false,
    icon: 'map-pin',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.COUNTRY,
    name: 'Country',
    name_ar: 'بلد الإقامة',
    placeholder: 'Select country',
    help_text: 'Collect country of residence.',
    coming_soon: false,
    icon: 'globe',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.current_age,
    name: 'Current age',
    name_ar: 'العمر الحالي',
    placeholder: 'e.g. 32',
    help_text: 'Collect current age.',
    coming_soon: true,
    icon: 'age',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.data_consent,
    name: 'Data consent',
    name_ar: 'موافقة البيانات',
    placeholder: '',
    help_text: 'Collect data consent agreement.',
    coming_soon: true,
    icon: 'data-consent',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.DATE,
    name: 'Date',
    name_ar: 'تاريخ',
    placeholder: 'Select date',
    help_text: 'Collect a date value.',
    coming_soon: false,
    icon: 'calendar',
    estimated_filling_time_seconds: 15
  },
  {
    form_element_type_id: ComingSoonFieldTypes.date_of_birth,
    name: 'Date of birth',
    name_ar: 'تاريخ الميلاد',
    placeholder: 'Select date of birth',
    help_text: 'Collect date of birth.',
    coming_soon: true,
    icon: 'date-of-birth',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.document_number,
    name: 'Document number',
    name_ar: 'رقم المستند',
    placeholder: 'e.g. DOC-001',
    help_text: 'Collect a document number.',
    coming_soon: true,
    icon: 'hashtag',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.education_level,
    name: 'Education level',
    name_ar: 'المستوى التعليمي',
    placeholder: 'Select education level',
    help_text: 'Collect education level.',
    coming_soon: true,
    icon: 'education-level',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.EMAIL,
    name: 'Email',
    name_ar: 'البريد الإلكتروني',
    placeholder: 'e.g. name@company.com',
    help_text: 'Collect a valid email address.',
    coming_soon: false,
    icon: 'envelope',
    estimated_filling_time_seconds: 15
  },
  {
    form_element_type_id: ComingSoonFieldTypes.error_message,
    name: 'Error message',
    name_ar: 'رسالة خطأ',
    placeholder: 'Enter error message',
    help_text: 'Show an error message. Does not add to estimated completion time.',
    coming_soon: true,
    icon: 'error-message',
    estimated_filling_time_seconds: 0
  },
  {
    form_element_type_id: ComingSoonFieldTypes.eye_colour,
    name: 'Eye colour',
    name_ar: 'لون العين',
    placeholder: 'Select eye colour',
    help_text: 'Collect eye colour.',
    coming_soon: true,
    icon: 'eye-colour',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.flag,
    name: 'Flag',
    name_ar: 'علم',
    placeholder: 'Select flag',
    help_text: 'Collect a flag value.',
    coming_soon: true,
    icon: 'flag',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.FULLNAME,
    name: 'Full name',
    name_ar: 'الاسم بالكامل',
    placeholder: 'e.g. Jane Doe',
    help_text: 'Collect the attendee’s full name.',
    coming_soon: false,
    icon: 'user',
    estimated_filling_time_seconds: 25
  },
  {
    form_element_type_id: PersonaFieldTypes.GENDER,
    name: 'Gender',
    name_ar: 'النوع',
    placeholder: 'Select gender',
    help_text: 'Collect gender selection.',
    coming_soon: false,
    icon: 'gender',
    estimated_filling_time_seconds: 10
  },
  {
    form_element_type_id: ComingSoonFieldTypes.hair_colour,
    name: 'Hair colour',
    name_ar: 'لون الشعر',
    placeholder: 'Select hair colour',
    help_text: 'Collect hair colour.',
    coming_soon: true,
    icon: 'hair-colour',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.height,
    name: 'Height',
    name_ar: 'الطول',
    placeholder: 'e.g. 170 cm',
    help_text: 'Collect height.',
    coming_soon: true,
    icon: 'height',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.IDENTITY,
    name: 'ID/Passport number',
    name_ar: 'رقم البطاقة الشخصية/ رقم جواز السفر',
    placeholder: 'e.g. A1234567',
    help_text: 'Collect ID or passport number.',
    coming_soon: false,
    icon: 'id',
    estimated_filling_time_seconds: 25
  },
  {
    form_element_type_id: PersonaFieldTypes.INFORMATION_MESSAGE,
    name: 'Info message',
    name_ar: 'رسالة توضيحية',
    placeholder: 'Enter info message',
    help_text: 'Show an informational message. Does not add to estimated completion time.',
    coming_soon: false,
    icon: 'info',
    estimated_filling_time_seconds: 0
  },
  {
    form_element_type_id: PersonaFieldTypes.JOB_TITLE,
    name: 'Job title',
    name_ar: 'المسمى الوظيفي',
    placeholder: 'e.g. Product Manager',
    help_text: 'Collect the attendee’s job title.',
    coming_soon: false,
    icon: 'briefcase',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.LINE_SEPARATOR,
    name: 'Line separator',
    name_ar: 'فاصل سطر',
    placeholder: '',
    help_text: 'Add a visual separator. Does not add to estimated completion time.',
    coming_soon: false,
    icon: 'separator',
    estimated_filling_time_seconds: 0
  },
  {
    form_element_type_id: ComingSoonFieldTypes.money,
    name: 'Money',
    name_ar: 'المبلغ',
    placeholder: 'e.g. 250',
    help_text: 'Collect a money amount.',
    coming_soon: true,
    icon: 'dollar',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.CHECKBOX,
    name: 'Multiple select',
    name_ar: 'اختيار متعدد',
    placeholder: 'Select options',
    help_text: 'Let attendees pick multiple options.',
    coming_soon: false,
    icon: 'multiple-select',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.nationality,
    name: 'Nationality',
    name_ar: 'الجنسية',
    placeholder: 'Select nationality',
    help_text: 'Collect nationality.',
    coming_soon: true,
    icon: 'globe',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.number,
    name: 'Number',
    name_ar: 'رقم',
    placeholder: 'e.g. 10',
    help_text: 'Collect a number value.',
    coming_soon: true,
    icon: 'hashtag',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.NUMERIC,
    name: 'Numeric',
    name_ar: 'حقل رقمي',
    placeholder: 'e.g. 10',
    help_text: 'Collect a numeric value.',
    coming_soon: false,
    icon: 'hashtag',
    estimated_filling_time_seconds: 12
  },
  {
    form_element_type_id: PersonaFieldTypes.ORGANIZATION,
    name: 'Organization',
    name_ar: 'المنظمة',
    placeholder: 'e.g. MICEtribe',
    help_text: 'Collect the attendee’s organization.',
    coming_soon: false,
    icon: 'building',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.PHONE,
    name: 'Phone number',
    name_ar: 'رقم الهاتف',
    placeholder: 'e.g. +1 555 000 0000',
    help_text: 'Collect a phone number with country code.',
    coming_soon: false,
    icon: 'phone',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.PLAIN_TEXT,
    name: 'Plain text',
    name_ar: 'نص عادي',
    placeholder: 'Enter text',
    help_text: 'Collect free-form text.',
    coming_soon: false,
    icon: 'plain-text',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.shoe_size,
    name: 'Shoe size',
    name_ar: 'مقاس الحذاء',
    placeholder: 'e.g. 42',
    help_text: 'Collect shoe size.',
    coming_soon: true,
    icon: 'shoe-size',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.RADIO,
    name: 'Single select',
    name_ar: 'اختيار واحد',
    placeholder: 'Select one option',
    help_text: 'Let attendees pick one option.',
    coming_soon: false,
    icon: 'single-select',
    estimated_filling_time_seconds: 10
  },
  {
    form_element_type_id: ComingSoonFieldTypes.skin_tone,
    name: 'Skin tone',
    name_ar: 'لون البشرة',
    placeholder: 'Select skin tone',
    help_text: 'Collect skin tone.',
    coming_soon: true,
    icon: 'skin-tone',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.STAR_RATING,
    name: 'Star rating',
    name_ar: 'تقييم بالنجوم',
    placeholder: 'Select rating',
    help_text: 'Collect a star rating.',
    coming_soon: false,
    icon: 'star',
    estimated_filling_time_seconds: 8
  },
  {
    form_element_type_id: ComingSoonFieldTypes.t_shirt_size,
    name: 'T-shirt size',
    name_ar: 'مقاس التيشيرت',
    placeholder: 'Select size',
    help_text: 'Collect T-shirt size.',
    coming_soon: true,
    icon: 'tshirt',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.upload_document,
    name: 'Upload document',
    name_ar: 'رفع مستند',
    placeholder: 'Upload a document',
    help_text: 'Let attendees upload a document.',
    coming_soon: true,
    icon: 'upload-document',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.UPLOAD,
    name: 'Upload field',
    name_ar: 'رفع ملف',
    placeholder: 'Upload a file',
    help_text: 'Let attendees upload a file.',
    coming_soon: false,
    icon: 'upload',
    estimated_filling_time_seconds: 30
  },
  {
    form_element_type_id: ComingSoonFieldTypes.upload_photo,
    name: 'Upload photo',
    name_ar: 'رفع صورة',
    placeholder: 'Upload a photo',
    help_text: 'Let attendees upload a photo.',
    coming_soon: true,
    icon: 'upload-photo',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.url,
    name: 'URL',
    name_ar: 'رابط',
    placeholder: 'e.g. https://example.com',
    help_text: 'Collect a generic URL.',
    coming_soon: true,
    icon: 'link',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: ComingSoonFieldTypes.waist_circumference,
    name: 'Waist circumference',
    name_ar: 'محيط الخصر',
    placeholder: 'e.g. 80 cm',
    help_text: 'Collect waist circumference.',
    coming_soon: true,
    icon: 'waist-circumference',
    estimated_filling_time_seconds: 20
  },
  {
    form_element_type_id: PersonaFieldTypes.WARNING_MESSAGE,
    name: 'Warning message',
    name_ar: 'رسالة تحذيرية',
    placeholder: 'Enter warning message',
    help_text: 'Show a warning message. Does not add to estimated completion time.',
    coming_soon: false,
    icon: 'warning',
    estimated_filling_time_seconds: 0
  },
  {
    form_element_type_id: PersonaFieldTypes.URL,
    name: 'Website',
    name_ar: 'موقع الكتروني',
    placeholder: 'e.g. https://example.com',
    help_text: 'Collect a website URL.',
    coming_soon: false,
    icon: 'link',
    estimated_filling_time_seconds: 15
  },
  {
    form_element_type_id: ComingSoonFieldTypes.weight,
    name: 'Weight',
    name_ar: 'الوزن',
    placeholder: 'e.g. 70 kg',
    help_text: 'Collect weight.',
    coming_soon: true,
    icon: 'weight',
    estimated_filling_time_seconds: 20
  }
]

const _fieldsByTypeId = new Map<FormFieldTypeId, FormFieldLibraryItem>(FORM_FIELD_LIBRARY.map((field) => [field.form_element_type_id, field]))

export function getFormFieldByTypeId(typeId: FormFieldTypeId): FormFieldLibraryItem | undefined {
  return _fieldsByTypeId.get(typeId)
}

/**
 * Build Frequently used list: fixed core first, then API popular types, unique, max 8.
 * Falls back to defaults when `apiTypeIds` is null/undefined (API failed).
 */
export function buildFrequentlyUsedFields(apiTypeIds: number[] | null | undefined): FormFieldLibraryItem[] {
  if (apiTypeIds == null) {
    return DEFAULT_FREQUENTLY_USED_TYPE_IDS.map((id) => getFormFieldByTypeId(id)).filter((f): f is FormFieldLibraryItem => !!f)
  }

  const includedFieldTypeIds = new Set<FormFieldTypeId>()
  const result: FormFieldLibraryItem[] = []

  for (const typeId of FIXED_FREQUENTLY_USED_TYPE_IDS) {
    const field = getFormFieldByTypeId(typeId)
    if (!field || includedFieldTypeIds.has(typeId)) continue
    includedFieldTypeIds.add(typeId)
    result.push(field)
  }

  for (const typeId of apiTypeIds) {
    if (result.length >= MAX_FREQUENTLY_USED) break
    if (includedFieldTypeIds.has(typeId)) continue
    const field = getFormFieldByTypeId(typeId)
    if (!field || field.coming_soon) continue
    includedFieldTypeIds.add(typeId)
    result.push(field)
  }

  return result
}
